<div class="row voucher-fields" [formGroup]="form">

  <!-- How many vouchers (and, for a contest prize, how many winners get one) -->
  <div class="col-lg-6" *ngIf="showQuantity">
    <div class="form-group">
      <label class="form-label" for="vf-quantity">
        {{ quantityLabelKey | translate }}<span>*</span>
      </label>
      <input id="vf-quantity" type="number" class="form-control" formControlName="quantity" step="1"
        [attr.min]="minQuantity" [attr.max]="maxQuantity"
        [placeholder]="'vouchers.fields.quantity.placeholder' | translate" />
      <small class="field-hint">{{ quantityHintKey | translate: { max: maxQuantity | number } }}</small>
      <small class="error text-danger" *ngIf="errorFor('quantity') as err">
        {{ err | translate: { max: maxQuantity | number, min: minQuantity } }}
      </small>
    </div>
  </div>

  <!-- Expiry -->
  <div class="col-lg-6">
    <div class="form-group">
      <label class="form-label">
        {{ 'vouchers.fields.expiry.label' | translate }}<span>*</span>
      </label>
      <nz-date-picker formControlName="voucher_expires_at" nzFormat="dd/MM/yyyy" class="custom-select" style="width: 100%"
        [nzDisabled]="rulesLocked" [nzDisabledDate]="disabledDate"
        [nzPlaceHolder]="'vouchers.fields.expiry.placeholder' | translate">
      </nz-date-picker>
      <small class="field-hint">{{ 'vouchers.fields.expiry.hint' | translate }}</small>
      <small class="error text-danger" *ngIf="errorFor('voucher_expires_at') as err">
        {{ err | translate }}
      </small>
    </div>
  </div>

  <!-- Value and currency -->
  <div class="col-lg-6">
    <div class="form-group">
      <label class="form-label" for="vf-value">
        {{ 'vouchers.fields.value.label' | translate }}<span>*</span>
      </label>
      <input id="vf-value" type="number" class="form-control" formControlName="voucher_value" min="0.01" step="0.01"
        [readonly]="rulesLocked" [placeholder]="'vouchers.fields.value.placeholder' | translate" />
      <small class="field-hint">{{ 'vouchers.fields.value.hint' | translate }}</small>
      <small class="error text-danger" *ngIf="errorFor('voucher_value') as err">
        {{ err | translate }}
      </small>
    </div>
  </div>

  <div class="col-lg-6">
    <div class="form-group">
      <label class="form-label">
        {{ 'vouchers.fields.currency.label' | translate }}<span>*</span>
      </label>
      <nz-select formControlName="voucher_currency" style="width: 100%" nzShowSearch [nzDisabled]="rulesLocked"
        [nzPlaceHolder]="'vouchers.fields.currency.placeholder' | translate" [nzOptionOverflowSize]="5">
        <nz-option *ngFor="let c of currencies" [nzValue]="c.code" [nzLabel]="optionLabel(c)"></nz-option>
      </nz-select>
      <small class="field-hint" *ngIf="rateHint as hint">
        {{ hint.key | translate: hint.params }}
      </small>
      <small class="error text-danger" *ngIf="errorFor('voucher_currency') as err">
        {{ err | translate }}
      </small>
    </div>
  </div>

  <!-- Single or multiple use -->
  <div class="col-lg-12">
    <div class="form-group">
      <label class="form-label">
        {{ 'vouchers.fields.usage.label' | translate }}
        <span nz-tooltip nzTooltipTitle="{{ 'vouchers.fields.usage.tooltip' | translate }}" nzTooltipPlacement="top">
          <i class="fa-solid fa-circle-info info"></i>
        </span>
      </label>
      <div class="select-options" role="radiogroup" [class.rules-locked]="rulesLocked">
        <a href="javascript:void(0)" class="option" role="radio" [attr.aria-checked]="!isMulti"
          [class.active]="!isMulti" (click)="setUsage('SINGLE_USE')">
          {{ 'vouchers.fields.usage.single' | translate }}
        </a>
        <a href="javascript:void(0)" class="option" role="radio" [attr.aria-checked]="isMulti"
          [class.active]="isMulti" (click)="setUsage('MULTI_USE')">
          {{ 'vouchers.fields.usage.multi' | translate }}
        </a>
      </div>
      <small class="field-hint">
        {{ (isMulti ? 'vouchers.fields.usage.multiHint' : 'vouchers.fields.usage.singleHint') | translate }}
      </small>
    </div>
  </div>

  <!-- Multi-use only: carry the balance over, and how many purchases -->
  <ng-container *ngIf="isMulti">
    <div class="col-lg-6">
      <div class="form-group">
        <label class="form-label" for="vf-carry">
          {{ 'vouchers.fields.carry.label' | translate }}
          <span nz-tooltip nzTooltipTitle="{{ 'vouchers.fields.carry.tooltip' | translate }}"
            nzTooltipPlacement="top">
            <i class="fa-solid fa-circle-info info"></i>
          </span>
        </label>
        <div class="d-flex align-items-center gap-3">
          <nz-switch id="vf-carry" formControlName="carry_over_balance" [nzDisabled]="rulesLocked"></nz-switch>
          <span class="switch-state">
            {{ (carry ? 'vouchers.fields.carry.on' : 'vouchers.fields.carry.off') | translate }}
          </span>
        </div>
      </div>
    </div>

    <div class="col-lg-6">
      <div class="form-group">
        <label class="form-label" for="vf-max-uses">
          {{ 'vouchers.fields.maxUses.label' | translate }}<span *ngIf="!carry">*</span>
        </label>
        <input id="vf-max-uses" type="number" class="form-control" formControlName="max_uses" step="1"
          [attr.min]="minUses" [attr.max]="maxUses" [readonly]="rulesLocked"
          [placeholder]="(carry ? 'vouchers.fields.maxUses.placeholderOptional' : 'vouchers.fields.maxUses.placeholder') | translate" />
        <small class="field-hint">
          {{ (carry ? 'vouchers.fields.maxUses.hintOptional' : 'vouchers.fields.maxUses.hintRequired') | translate: { min: minUses, max: maxUses } }}
        </small>
        <small class="error text-danger" *ngIf="errorFor('max_uses') as err">
          {{ err | translate: { min: minUses, max: maxUses } }}
        </small>
      </div>
    </div>
  </ng-container>

  <!-- What the rules above mean in practice -->
  <div class="col-lg-12">
    <div class="voucher-explain" role="note">
      <i class="fa-solid fa-circle-info"></i>
      <div>
        <strong>{{ 'vouchers.explain.title' | translate }}</strong>
        <p>{{ explanation.key | translate: explanation.params }}</p>
      </div>
    </div>
  </div>
</div>
